import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useHallOfFame } from '../api/hooks';
import { Avatar } from '../components/Avatar';
import { BackIcon, CrownIcon } from '../components/icons';
import { ErrorView } from '../components/StateViews';
import { backButton } from '../telegram';
import { track } from '../analytics';
import { plural, shortDate } from '../format';
import type { HallSeason } from '../api/types';

const SEASONS_PER_CHAPTER = 10;

/** Номер сезона внутри главы — как в переключателе на рейтинге. */
const inChapter = (id: number) => ((id - 1) % SEASONS_PER_CHAPTER) + 1;

/**
 * «Зал славы» — список сезонов с чемпионами (design_handoff_season_winners §1).
 * Экран пришпилен к тёмной теме (spot-dark): дизайн золотой и дарк-онли.
 */
export function HallOfFameScreen() {
  const navigate = useNavigate();
  const hall = useHallOfFame();

  useEffect(() => {
    const back = () => navigate(-1);
    backButton.show(back);
    return () => backButton.hide(back);
  }, [navigate]);

  const seasons = hall.data ?? [];
  // Действующий чемпион = самый свежий завершённый сезон с чемпионом (DESC).
  const reigning = seasons.find((s) => s.status === 'finished' && s.champion);
  const finishedCount = seasons.filter((s) => s.status === 'finished').length;
  const championsCount = seasons.filter((s) => s.champion).length;
  // Незавершённый — в конец списка (хендофф §61).
  const rows = [...seasons].sort((a, b) => {
    if (a.status !== b.status) return a.status === 'active' ? 1 : -1;
    return b.seasonId - a.seasonId;
  });

  return (
    <div className="hof-screen" data-theme="dark">
      <div className="subhead hof-head">
        <button className="iconbtn" onClick={() => navigate(-1)} aria-label="Назад">
          <BackIcon />
        </button>
        <div>
          <div className="hof-title">Зал славы</div>
          <div className="hof-caption">
            {seasons.length} {plural(seasons.length, ['сезон', 'сезона', 'сезонов'])} ·{' '}
            {championsCount} {plural(championsCount, ['чемпион', 'чемпиона', 'чемпионов'])}
          </div>
        </div>
      </div>

      {hall.isLoading ? (
        <HallSkeleton />
      ) : hall.isError ? (
        <ErrorView message={(hall.error as Error)?.message} />
      ) : finishedCount === 0 ? (
        <div className="hof-empty">Первый чемпион появится после финала Сезона 1</div>
      ) : (
        <>
          {reigning && reigning.champion && (
            <Link
              className="hof-hero"
              to={`/hall-of-fame/${reigning.seasonId}`}
              onClick={() => track('champion_open', { seasonId: reigning.seasonId, from: 'hero' })}
            >
              <img className="hof-hero-mark" src="/logo.png" alt="" />
              <div className="hof-overline hof-overline--gold">Действующий чемпион</div>
              <div className="hof-hero-row">
                <span className="hof-ring hof-ring--hero">
                  <Avatar name={reigning.champion.name} src={reigning.champion.photoUrl} size={54} />
                  <span className="hof-crown">
                    <CrownIcon />
                  </span>
                </span>
                <div>
                  <div className="hof-hero-name">{reigning.champion.name}</div>
                  <div className="hof-hero-season">Чемпион Сезона {inChapter(reigning.seasonId)}</div>
                </div>
              </div>
              <div className="hof-hero-stats">
                <div>
                  <b>{reigning.champion.points}</b>
                  <span>очков</span>
                </div>
                <div>
                  <b>{reigning.champion.wins}</b>
                  <span>{plural(reigning.champion.wins, ['победа', 'победы', 'побед'])}</span>
                </div>
                <div>
                  <b>{reigning.champion.tournaments}</b>
                  <span>{plural(reigning.champion.tournaments, ['турнир', 'турнира', 'турниров'])}</span>
                </div>
              </div>
            </Link>
          )}

          <div className="hof-overline hof-list-label">Все сезоны</div>
          <div className="hof-list">
            {rows.map((s) => (
              <SeasonRow key={s.seasonId} s={s} isReigning={s.seasonId === reigning?.seasonId} />
            ))}
            <div className="hof-fade" />
          </div>
        </>
      )}
    </div>
  );
}

function SeasonRow({ s, isReigning }: { s: HallSeason; isReigning: boolean }) {
  const n = inChapter(s.seasonId);

  if (s.status === 'active' || !s.champion) {
    const sub =
      s.status === 'active'
        ? s.finaleAt
          ? `Сезон идёт · финал ${shortDate(s.finaleAt)}`
          : 'Сезон идёт'
        : 'Сезон завершён';
    return (
      <div className="hof-row hof-row--pending">
        <div className="hof-num">
          <b>{n}</b>
          <span>сезон</span>
        </div>
        <span className="hof-ava-dash">?</span>
        <div className="hof-row-main">
          <div className="nm">Чемпион не определён</div>
          <div className="sub">{sub}</div>
        </div>
      </div>
    );
  }

  const c = s.champion;
  return (
    <Link
      className={`hof-row${isReigning ? ' hof-row--current' : ''}`}
      to={`/hall-of-fame/${s.seasonId}`}
      onClick={() => track('champion_open', { seasonId: s.seasonId, from: 'list' })}
    >
      <div className="hof-num">
        <b>{n}</b>
        <span>сезон</span>
      </div>
      <span className="hof-ring hof-ring--row">
        <Avatar name={c.name} src={c.photoUrl} size={30} />
      </span>
      <div className="hof-row-main">
        <div className="nm">{c.name}</div>
        <div className="sub">
          {c.tournaments} {plural(c.tournaments, ['турнир', 'турнира', 'турниров'])} · {c.wins}{' '}
          {plural(c.wins, ['победа', 'победы', 'побед'])}
        </div>
      </div>
      <div className="hof-row-pts">{c.points}</div>
    </Link>
  );
}

function HallSkeleton() {
  return (
    <div>
      <div className="skeleton" style={{ height: 148, borderRadius: 20, marginBottom: 18 }} />
      {Array.from({ length: 4 }, (_, i) => (
        <div key={i} className="skeleton" style={{ height: 54, borderRadius: 10, marginBottom: 8 }} />
      ))}
    </div>
  );
}
